/**
 * PATH: packages/ui/src/components/settings/useKeychordCapture.ts
 * FILE_ID: UI-SETTINGS-KEYCHORD-CAPTURE-001
 * PURPOSE: Capture-mode state machine for the shortcut editor's "press keys to
 *          remap" affordance — while active it intercepts the next keychord
 *          (capture phase, preventing it from triggering anything else) and
 *          reports the normalized binding for the row being edited.
 * WHY: Remapping must grab the raw keydown before any app shortcut fires, and
 *      must ignore modifier-only presses until a real key lands. Centralizing
 *      this keeps ShortcutCustomizer.tsx lean + the listener lifecycle correct.
 *
 * FLOW:
 *   start(id) → listen (capture) → keydown → eventToBinding → onCapture(id, b)
 *            Escape (no mods) → cancel
 *
 * DEPENDENCIES:
 *   - ../../shortcuts: eventToBinding (pure event → binding)
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { eventToBinding, type ShortcutPlatform } from '../../shortcuts'

export interface UseKeychordCaptureResult {
  /** The action id currently capturing, or null when idle. */
  capturingId: string | null
  /** Begin capturing for an action id. */
  start: (id: string) => void
  /** Abort capture without changing anything. */
  cancel: () => void
}

export function useKeychordCapture(
  platform: ShortcutPlatform,
  onCapture: (id: string, binding: string) => void,
): UseKeychordCaptureResult {
  const [capturingId, setCapturingId] = useState<string | null>(null)

  // Keep the callback in a ref so the document listener never goes stale and we
  // don't re-subscribe on every parent render.
  const onCaptureRef = useRef(onCapture)
  useEffect(() => {
    onCaptureRef.current = onCapture
  }, [onCapture])

  const start = useCallback((id: string) => setCapturingId(id), [])
  const cancel = useCallback(() => setCapturingId(null), [])

  useEffect(() => {
    if (!capturingId) return
    const handler = (event: KeyboardEvent) => {
      // Swallow the chord so it never triggers a real shortcut while editing.
      event.preventDefault()
      event.stopPropagation()

      const noMods = !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey
      if (event.key === 'Escape' && noMods) {
        setCapturingId(null)
        return
      }

      const binding = eventToBinding(event, platform)
      if (!binding) return // still only modifiers held - keep waiting

      onCaptureRef.current(capturingId, binding)
      setCapturingId(null)
    }
    // Capture phase + keydown so we win over app-level handlers.
    window.addEventListener('keydown', handler, true)
    return () => window.removeEventListener('keydown', handler, true)
  }, [capturingId, platform])

  return { capturingId, start, cancel }
}
